// A spirit's powers at the table (issue 1172; docs/MRCS/shadowrun-3.md §15): each power its entry lists is an action on
// its token, assisted, not settled — the table rolls the power's test with the target's resistance from its sheet and
// shows the power's rule with the result; the DM settles the effect. How each power is resolved is the content's
// (`roll` on the power's entry), and which fields name the spirit's powers, its Engulf element and its Force the
// system's (`features.combat.conjuring.powers`). Nothing here names a game's attribute: the power's entry names them,
// and they are read from the tokens' sheet numbers of the spirit's purpose.
//
// - `opposed`: the spirit's `dice` attribute (times `dice_multiplier`) against the target's `resist` attribute as TN, the
//   target's `resist` against the spirit's attribute; the net successes are what the rule counts (Fear's turns).
// - `target_test`: the target rolls `resist` (the greater with `resist_best`) against the spirit's `target_number`
//   attribute times its multiplier; no success, and the power's `on_fail` holds.
// - `damage`: the target resists the power's code (the spirit's attribute as its Power) with `resist`.
// - `melee` (Engulf): a melee attack, offered as a weapon row of the spirit (`engulfWeapon`) in the Melee attack form,
//   staged, resisted and applied as any attack is.
// - `spell` and `none`: no test; the rule is shown and the DM settles it.
//
// The record is a success test's with the power beside it (`TestFacts.power`). Pure: the server reads the entries and
// the sheets and hands them in, and the generator; a test hands a scripted list.
import { MAX_POOL } from '../dice/pool.js';
import { sheetRating } from './staged.js';
import { ADDRESS_RE, makeTest, sideLine, sideResult } from './test.js';
const ROLLS = ['opposed', 'target_test', 'melee', 'damage', 'spell', 'none'];
/** The rolls the table throws as a power's own test. */
export const POWER_TESTS = ['opposed', 'target_test', 'damage'];
export const POWER_LIMITS = { name: 80, rule: 400, note: 120, code: 24, multiplier: 10 };
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const text = (v, max) => (typeof v === 'string' && v.trim() && !LINE_BAD.test(v) ? v.trim().slice(0, max) : undefined);
const key = (v) => (typeof v === 'string' && KEY_RE.test(v.trim().toLowerCase()) ? v.trim().toLowerCase() : undefined);
const mult = (v) => (typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= POWER_LIMITS.multiplier ? v : 1);
const slugOf = (l) => {
    const s = isObj(l) && typeof l.slug === 'string' ? l.slug : typeof l === 'string' ? l.split('/').at(-1) : null;
    return s && /^[a-z0-9][a-z0-9-]{0,127}$/.test(s) ? s : null;
};
function damageFrom(v) {
    if (!isObj(v))
        return undefined;
    const power = key(v.power);
    const level = text(v.level, 4);
    if (!power || !level)
        return undefined;
    const resist = Array.isArray(v.resist) ? v.resist.map(key).filter((x) => Boolean(x)).slice(0, 4) : undefined;
    return {
        power, level, stun: v.stun === true, armour: key(v.armour) && key(v.armour) !== 'none' ? key(v.armour) : null,
        ...(typeof v.power_bonus === 'number' && Number.isInteger(v.power_bonus) && Math.abs(v.power_bonus) <= 10 ? { bonus: v.power_bonus } : {}),
        ...(typeof v.power_per_action === 'number' && Number.isInteger(v.power_per_action) && Math.abs(v.power_per_action) <= 10 ? { perAction: v.power_per_action } : {}),
        ...(resist?.length ? { resist } : {}), ...(v.resist_best === true ? { resistBest: true } : {}),
        ...(text(v.note, POWER_LIMITS.note) ? { note: text(v.note, POWER_LIMITS.note) } : {}),
    };
}
/** A power's entry out of the site's detail (its fields and `attrs`, its links as slugs), or null. Pure. */
export function powerEntryFrom(body, ref) {
    if (!isObj(body) || !ADDRESS_RE.test(ref))
        return null;
    const a = { ...body, ...(isObj(body.attrs) ? body.attrs : {}) };
    const name = text(a.name, POWER_LIMITS.name);
    const roll = ROLLS.find((r) => r === a.roll) ?? 'none';
    if (!name)
        return null;
    const linked = isObj(body.links) ? body.links.resist : a.resist;
    const resist = (Array.isArray(linked) ? linked : linked === undefined ? [] : [linked]).map(slugOf).filter((x) => x !== null).map((s) => s.replace(/-/g, '_')).slice(0, 4);
    const byElement = {};
    if (isObj(a.damage_by_element))
        for (const [k, v] of Object.entries(a.damage_by_element)) {
            const d = damageFrom(v);
            const kk = text(k, 24);
            if (d && kk)
                byElement[kk] = d;
        }
    const damage = damageFrom(a.damage);
    return {
        ref, name, roll, effect: text(a.effect, POWER_LIMITS.rule) ?? '',
        ...(text(a.action, 40) ? { action: text(a.action, 40) } : {}), ...(text(a.range, 40) ? { range: text(a.range, 40) } : {}), ...(text(a.duration, 40) ? { duration: text(a.duration, 40) } : {}),
        ...(key(a.dice) ? { dice: key(a.dice) } : {}), diceMultiplier: mult(a.dice_multiplier),
        ...(key(a.target_number) ? { targetNumber: key(a.target_number) } : {}), targetNumberMultiplier: mult(a.target_number_multiplier),
        resist, resistBest: a.resist_best === true,
        ...(text(a.on_fail, POWER_LIMITS.note) ? { onFail: text(a.on_fail, POWER_LIMITS.note) } : {}),
        ...(text(a.lasts, POWER_LIMITS.note) ? { lasts: text(a.lasts, POWER_LIMITS.note) } : {}),
        ...(damage ? { damage } : {}), ...(Object.keys(byElement).length ? { damageByElement: byElement } : {}),
    };
}
/** A key as words for a person: "willpower" → "Willpower". */
export const fieldLabel = (k) => k.split('_').map((w) => (w ? `${w[0].toUpperCase()}${w.slice(1)}` : w)).join(' ');
/** A creature's attribute from its sheet numbers of the purpose, or null. */
const ratingOf = (sheet, purpose, which) => (which ? sheetRating(sheet, [{ purpose, which }]) : null);
/** The target's resistance from its sheet: the first attribute the power names, or the greatest with `resist_best`. */
export function powerResistance(sheet, purpose, resist, best) {
    let got = null;
    for (const w of resist) {
        const r = ratingOf(sheet, purpose, w);
        if (r === null)
            continue;
        if (!best)
            return { which: w, rating: r };
        if (!got || r > got.rating)
            got = { which: w, rating: r };
    }
    return got;
}
/**
 * Engulf as a weapon of the spirit (§15): the code of its element's damage at the spirit's Force (`power` the Force's
 * own field), written as the system's codes write one ("4M", "4S Stun" for the lesser kind), named with the element; and
 * what the form cannot carry said in words (the armour that counts, the Power each action adds, a bonus with another
 * power). Null when the element has no damage the power's entry gives.
 */
export function engulfWeapon(power, element, force, _forceField, codes, stunKind) {
    const d = element ? power.damageByElement?.[element] ?? power.damage : power.damage;
    if (!d || force === null)
        return null;
    const level = codes.levels.find((l) => l.abbr === d.level || l.key === d.level.toLowerCase());
    if (!level)
        return null;
    const code = `${Math.min(99, force)}${level.abbr}${d.stun && stunKind ? ` ${stunKind.label}` : ''}`;
    const notes = [
        d.armour ? `${fieldLabel(d.armour)} armour counts` : 'no armour counts',
        ...(d.perAction ? [`+${d.perAction} Power for each action after the first`] : []),
        ...(d.resist?.length ? [`resisted with ${d.resistBest ? 'the greater of ' : ''}${d.resist.map(fieldLabel).join(d.resistBest ? ' and ' : ' or ')}`] : []),
        ...(d.note ? [d.note] : []),
    ];
    return { name: `${power.name}${element ? ` (${element})` : ''}`.slice(0, POWER_LIMITS.name), code, note: notes.join('; ').slice(0, POWER_LIMITS.rule) };
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function parsePowerRequest(v) {
    if (!isObj(v) || v.kind !== 'spirit.power' || Object.keys(v).some((k) => !['kind', 'spirit', 'power', 'target', 'rating'].includes(k)))
        return null;
    if (typeof v.spirit !== 'string' || !UUID_RE.test(v.spirit) || typeof v.power !== 'string' || !ADDRESS_RE.test(v.power))
        return null;
    if (v.target !== undefined && (typeof v.target !== 'string' || !UUID_RE.test(v.target)))
        return null;
    if (v.rating !== undefined && !(typeof v.rating === 'number' && Number.isInteger(v.rating) && v.rating >= 1 && v.rating <= MAX_POOL))
        return null;
    return { kind: 'spirit.power', spirit: v.spirit, power: v.power, ...(typeof v.target === 'string' ? { target: v.target } : {}), ...(typeof v.rating === 'number' ? { rating: v.rating } : {}) };
}
/** The power's test thrown (§15), or a refusal in words. */
export function makePower(ctx, rng) {
    const p = ctx.power;
    if (p.roll === 'melee')
        return `${p.name} is a melee attack: choose it as the spirit's weapon in the Melee attack form.`;
    if (p.roll === 'spell' || p.roll === 'none')
        return `${p.name} has no test of its own: the DM settles it by its rule.`;
    if (!ctx.target)
        return `${p.name} needs a target.`;
    const spiritRating = (which) => ratingOf(ctx.spirit.sheet, ctx.purpose, which);
    const resistList = p.roll === 'damage' && p.damage?.resist?.length ? p.damage.resist : p.resist;
    const best = p.roll === 'damage' && p.damage?.resist?.length ? Boolean(p.damage.resistBest) : p.resistBest;
    const read = powerResistance(ctx.target.sheet, ctx.purpose, resistList, best);
    const resist = read ?? (ctx.typed !== undefined ? { which: resistList[0] ?? '', rating: ctx.typed } : null);
    const resistLabel = resist?.which ? fieldLabel(resist.which) : 'Resistance';
    if (!resist)
        return `The target's ${resistList.length ? resistList.map(fieldLabel).join(' or ') : 'resistance'} is not on its sheet: type it.`;
    const targetName = ctx.target.who.name ?? 'The target';
    const base = { rule: p.effect, ...(p.onFail ? { onFail: p.onFail } : {}), ...(p.lasts ? { lasts: p.lasts } : {}), resist: resistLabel, target: { ...ctx.target.who } };
    let made;
    let facts;
    if (p.roll === 'opposed') {
        const own = spiritRating(p.dice);
        if (own === null)
            return `The spirit's ${p.dice ? fieldLabel(p.dice) : 'rating'} is not on its sheet.`;
        const dice = Math.min(MAX_POOL, own * p.diceMultiplier);
        made = makeTest({ kind: 'test.request', rated: { kind: 'attribute', name: fieldLabel(p.dice), rating: dice }, target: resist.rating, modifiers: [], open: false,
            opposed: { name: targetName.slice(0, POWER_LIMITS.name), rating: resist.rating, target: own, modifiers: [] }, private: false }, ctx.rules, ctx.spirit.who, rng, null, ctx.borrowed);
        facts = { ref: p.ref, name: p.name, roll: 'opposed', ...base };
    }
    else {
        const field = p.roll === 'damage' ? p.damage?.power : p.targetNumber;
        const own = spiritRating(field);
        if (own === null)
            return `The spirit's ${field ? fieldLabel(field) : 'rating'} is not on its sheet.`;
        const tn = Math.min(100, own * (p.roll === 'damage' ? 1 : p.targetNumberMultiplier));
        made = makeTest({ kind: 'test.request', rated: { kind: 'attribute', name: resistLabel, rating: resist.rating }, target: tn, modifiers: [], open: false, private: false }, ctx.rules, ctx.target.who, rng, null, ctx.borrowed);
        const level = p.damage ? ctx.codes.levels.find((l) => l.abbr === p.damage.level || l.key === p.damage.level.toLowerCase()) : undefined;
        facts = { ref: p.ref, name: p.name, roll: p.roll === 'damage' ? 'damage' : 'target_test', ...base, ...(p.roll === 'damage' && level ? { code: `${tn}${level.abbr}${p.damage.stun && ctx.stunKind ? ` ${ctx.stunKind.label}` : ''}`.slice(0, POWER_LIMITS.code) } : {}) };
    }
    if (typeof made === 'string')
        return made;
    return { ...made, power: facts };
}
// ── Stored records ────────────────────────────────────────────────────────
const okText = (v, max) => typeof v === 'string' && v.length <= max && !LINE_BAD.test(v);
/** Stored power facts beside a test's: exact shape or null. An opposed power has two sides; the others one. */
export function parsePowerFacts(v, sides) {
    if (!isObj(v) || Object.keys(v).some((k) => !['ref', 'name', 'roll', 'rule', 'onFail', 'lasts', 'code', 'resist', 'target'].includes(k)))
        return null;
    if (typeof v.ref !== 'string' || !ADDRESS_RE.test(v.ref) || !okText(v.name, POWER_LIMITS.name) || !v.name || !POWER_TESTS.includes(v.roll))
        return null;
    if (!okText(v.rule, POWER_LIMITS.rule) || !okText(v.resist, POWER_LIMITS.name) || !v.resist)
        return null;
    for (const k of ['onFail', 'lasts'])
        if (v[k] !== undefined && (!okText(v[k], POWER_LIMITS.note) || !v[k]))
            return null;
    if (v.code !== undefined && (!okText(v.code, POWER_LIMITS.code) || v.roll !== 'damage'))
        return null;
    const t = v.target;
    if (!isObj(t) || Object.keys(t).some((k) => k !== 'name' && k !== 'token') || !(t.name === null || (okText(t.name, POWER_LIMITS.name) && t.name)) || (t.token !== undefined && (typeof t.token !== 'string' || !UUID_RE.test(t.token))))
        return null;
    if (sides.length !== (v.roll === 'opposed' ? 2 : 1))
        return null;
    return {
        ref: v.ref, name: v.name, roll: v.roll, rule: v.rule, ...(typeof v.onFail === 'string' ? { onFail: v.onFail } : {}), ...(typeof v.lasts === 'string' ? { lasts: v.lasts } : {}),
        ...(typeof v.code === 'string' ? { code: v.code } : {}), resist: v.resist, target: { name: t.name, ...(typeof t.token === 'string' ? { token: t.token } : {}) },
    };
}
// ── The lines ─────────────────────────────────────────────────────────────
const plural = (n) => `${n} success${n === 1 ? '' : 'es'}`;
/**
 * Every line of a power's test (§15): each side's working, the result in the power's words for the DM to settle, and
 * the rule: "Fear: 4 net successes against the ganger's Willpower 3. Lasts: a Combat Turn per net success. The DM settles it."
 */
export function powerLines(f) {
    const p = f.power;
    const lines = f.sides.map((s) => sideLine({ open: f.open, rules: f.rules }, s));
    const who = p.target.name ?? 'The target';
    const whose = p.target.name ? `${p.target.name}'s` : "the target's";
    if (p.roll === 'opposed') {
        const [a, b] = f.sides.map((s) => sideResult(f, s).value);
        const net = a - b;
        const r = f.sides[1].rated.rating;
        lines.push(net > 0
            ? `${p.name}: ${net} net ${net === 1 ? 'success' : 'successes'} against ${whose} ${p.resist} ${r}.${p.lasts ? ` Lasts: ${p.lasts.charAt(0).toLowerCase()}${p.lasts.slice(1)} (${net}).` : ''} The DM settles it.`
            : `${p.name}: no net successes against ${whose} ${p.resist} ${r}; it has no effect.`);
    }
    else {
        const s = sideResult(f, f.sides[0]);
        if (p.roll === 'damage')
            lines.push(`${p.name}: ${who} resists ${p.code ?? 'the damage'} with ${s.disaster ? 'a disaster' : plural(s.value)}; every two successes stage it down a level. The DM applies it.`);
        else
            lines.push(s.value > 0 && !s.disaster ? `${p.name}: ${who} resists with ${plural(s.value)}.` : `${p.name}: ${who} fails.${p.onFail ? ` ${p.onFail}.` : ''} The DM settles it.`);
    }
    if (p.rule)
        lines.push(`Rule: ${p.rule}`);
    return lines;
}
//# sourceMappingURL=power.js.map